import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, Button, Grid, Divider, MenuItem, Card, CardContent, Paper, Chip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateHomepageConfig, fetchHomepageConfig } from "../../redux/slices/uiContentSlice";
import { DEFAULT_COLLECTIONS_QUERIES } from "../../redux/slices/collectionsSlice";
import SaveIcon from '@mui/icons-material/Save';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const HomepageContentEditor = ({ onBack }) => {
  const dispatch = useDispatch();
  const { homepageConfig, updateLoading } = useSelector((state) => state.uiContent);

  const [formData, setFormData] = useState(null);
  const imgRefs = useRef({});

  useEffect(() => {
    dispatch(fetchHomepageConfig());
  }, [dispatch]);

  useEffect(() => {
    if (homepageConfig) {
      const cloned = JSON.parse(JSON.stringify(homepageConfig));
      // Ensure collectionsQueries exists with defaults if not in DB yet
      if (!cloned.collectionsQueries) {
        cloned.collectionsQueries = JSON.parse(JSON.stringify(DEFAULT_COLLECTIONS_QUERIES));
      }
      
      // Migrate searchBarQueries
      if (!cloned.searchBarQueries) {
        cloned.searchBarQueries = {};
      }
      ['Women', 'Men', 'Kids'].forEach(gender => {
        let qs = cloned.searchBarQueries[gender];
        if (!Array.isArray(qs)) qs = [];
        qs = qs.slice(0, 3).map(q => {
          if (typeof q === 'string') {
            return { text: q, imageUrl: "", cropData: { unit: '%', x: 0, y: 0, width: 100, height: 100 } };
          }
          return { ...q, cropData: q.cropData || { unit: '%', x: 0, y: 0, width: 100, height: 100 } };
        });
        while (qs.length < 3) {
          qs.push({ text: "", imageUrl: "", cropData: { unit: '%', x: 0, y: 0, width: 100, height: 100 } });
        }
        cloned.searchBarQueries[gender] = qs;
      });

      setFormData(cloned);
    }
  }, [homepageConfig]);

  const handleQueryChange = (gender, index, field, value) => {
    const updated = { ...formData };
    updated.searchBarQueries[gender][index][field] = value;
    setFormData(updated);
  };

  const handleCropComplete = (gender, idx, pixelCrop) => {
    const img = imgRefs.current[`${gender}-${idx}`];
    if (img && pixelCrop.width && pixelCrop.height) {
      const canvas = document.createElement('canvas');
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      
      canvas.width = pixelCrop.width * scaleX;
      canvas.height = pixelCrop.height * scaleY;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(
        img,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY
      );
      
      try {
        const base64 = canvas.toDataURL('image/jpeg', 0.85);
        handleQueryChange(gender, idx, 'croppedImageUrl', base64);
      } catch (e) {
        console.error("Canvas CORS error, cannot generate preview", e);
      }
    }
  };


  const handleShowcaseChange = (showcaseIndex, field, value) => {
    const updated = { ...formData };
    updated.dynamicShowcase[showcaseIndex][field] = value;
    setFormData(updated);
  };

  const handleProductChange = (showcaseIndex, productIndex, field, value) => {
    const updated = { ...formData };
    updated.dynamicShowcase[showcaseIndex].products[productIndex][field] = value;
    setFormData(updated);
  };

  const handleCollectionChange = (index, field, value) => {
    const updated = { ...formData };
    updated.collectionsQueries = [...updated.collectionsQueries];
    updated.collectionsQueries[index] = { ...updated.collectionsQueries[index], [field]: value };
    setFormData(updated);
  };

  const handleSave = () => {
    dispatch(updateHomepageConfig(formData))
      .unwrap()
      .then(() => alert("Homepage configuration saved!"))
      .catch((err) => alert("Error saving configuration: " + err));
  };

  if (!formData) return <Box sx={{ p: 4 }}><Typography>Loading...</Typography></Box>;

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", pb: 10 }}>
      {/* STICKY HEADER */}
      <Paper
        elevation={0}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 2.5 },
          mb: { xs: 2, sm: 4 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          borderBottom: "1px solid #eaeaea",
          backgroundColor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)"
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#111", letterSpacing: "-0.02em" }}>
            Homepage Content
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Customize the homepage search queries, showcase blocks, and collection sections.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                width: { xs: "100%", sm: "auto" },
                borderColor: "#ccc",
                color: "#333",
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 1 },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#bbb" }
              }}
            >
              Back to Dashboard
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateLoading}
            startIcon={<SaveIcon />}
            sx={{
              width: { xs: "100%", sm: "auto" },
              backgroundColor: "#111",
              color: "#fff",
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 1 },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#333" }
            }}
          >
            {updateLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ px: { xs: 2, md: 4 } }}>
        {/* SECTION 1: SEARCH BAR QUERIES */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#222" }}>Search Bar Query Suggestions</Typography>
          <Grid container spacing={3}>
            {['Women', 'Men', 'Kids'].map((gender) => (
              <Grid item xs={12} md={4} key={gender}>
                <Card elevation={0} sx={{ border: "1px solid #eaeaea", borderRadius: "12px", height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: gender === 'Women' ? '#FF6B00' : gender === 'Men' ? '#2196F3' : '#4CAF50' }} />
                      <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>{gender}</Typography>
                    </Box>
                    {formData.searchBarQueries[gender]?.map((query, idx) => (
                      <Box key={idx} sx={{ mb: 4, p: 2, border: "1px solid #eee", borderRadius: "8px", backgroundColor: "#fafafa" }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Card {idx + 1}</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          label="Query Text"
                          variant="outlined"
                          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                          value={query.text || ''}
                          onChange={(e) => handleQueryChange(gender, idx, 'text', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Image URL"
                          variant="outlined"
                          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                          value={query.imageUrl || ''}
                          onChange={(e) => handleQueryChange(gender, idx, 'imageUrl', e.target.value)}
                        />
                        {query.imageUrl && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: "#666" }}>
                              Select Image Area (Crop)
                            </Typography>
                            <ReactCrop
                              crop={query.cropData || { unit: '%', x: 0, y: 0, width: 100, height: 100 }}
                              onChange={(c, percentCrop) => handleQueryChange(gender, idx, 'cropData', percentCrop)}
                              onComplete={(c) => handleCropComplete(gender, idx, c)}
                              aspect={0.785} // Portrait aspect ratio (e.g. 110/140)
                            >
                              <img 
                                ref={el => imgRefs.current[`${gender}-${idx}`] = el}
                                src={query.imageUrl} 
                                alt="preview" 
                                style={{ maxHeight: '300px', width: 'auto' }} 
                                crossOrigin="anonymous" 
                                onLoad={(e) => {
                                  // Trigger initial crop if not already done
                                  if (!query.croppedImageUrl) {
                                      // Create a default centered vertical crop
                                      const defaultWidth = 50;
                                      const defaultHeight = defaultWidth / 0.785;
                                      handleCropComplete(gender, idx, { 
                                        x: (e.target.width - e.target.width * (defaultWidth/100)) / 2, 
                                        y: (e.target.height - e.target.height * (defaultHeight/100)) / 2, 
                                        width: e.target.width * (defaultWidth/100), 
                                        height: e.target.height * (defaultHeight/100)
                                      });
                                  }
                                }}
                              />
                            </ReactCrop>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: "#999" }}>
                              Preview:
                            </Typography>
                            <Box sx={{ position: "relative", width: "90px", height: "115px", overflow: "hidden", borderRadius: "8px", mt: 0.5, backgroundColor: "#e0e0e0" }}>
                               {query.croppedImageUrl ? (
                                  <img 
                                    src={query.croppedImageUrl} 
                                    alt="crop preview" 
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover"
                                    }}
                                  />
                               ) : (
                                  <img src={query.imageUrl} alt="crop preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} crossOrigin="anonymous" />
                               )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 6, borderColor: "#eaeaea" }} />

        {/* SECTION 2: DYNAMIC SHOWCASE */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#222" }}>Dynamic Showcase Blocks</Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              These are the 5 alternating blocks shown in 'Handpik in Action' section. Each block shows 3 products.
            </Typography>
          </Box>

          {formData.dynamicShowcase.map((showcase, sIdx) => (
            <Card
              key={sIdx}
              elevation={0}
              sx={{
                mb: 4,
                border: "1px solid #eaeaea",
                borderRadius: "16px",
                overflow: "visible",
                position: "relative"
              }}
            >
              {/* Block Badge */}
              <Box sx={{ position: "absolute", top: -12, left: { xs: 16, sm: 24 } }}>
                <Chip
                  label={`Block ${sIdx + 1}`}
                  size="small"
                  sx={{ backgroundColor: "#111", color: "#fff", fontWeight: 600, fontSize: "12px", height: "24px" }}
                />
              </Box>

              <CardContent sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 5 } }}>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Query Text"
                      defaultValue={showcase.query}
                      onBlur={(e) => handleShowcaseChange(sIdx, "query", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      helperText="This query appears large on the screen"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Target Gender"
                      value={showcase.gender}
                      onChange={(e) => handleShowcaseChange(sIdx, "gender", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    >
                      <MenuItem value="Women">Women</MenuItem>
                      <MenuItem value="Men">Men</MenuItem>
                      <MenuItem value="Kids">Kids</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#f8f9fa", borderRadius: "12px", border: "1px dashed #ddd" }}>
                  <Typography sx={{ fontWeight: 600, mb: 3, fontSize: "14px", color: "#444", textTransform: "uppercase", letterSpacing: "1px" }}>
                    Products to Display
                  </Typography>
                  <Grid container spacing={3}>
                    {showcase.products.map((prod, pIdx) => (
                      <Grid item xs={12} md={4} key={pIdx}>
                        <Box sx={{ backgroundColor: "#fff", p: { xs: 2, sm: 2.5 }, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", border: "1px solid #eaeaea" }}>
                          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#888", mb: 2 }}>PRODUCT {pIdx + 1}</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            label="Product Title"
                            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                            defaultValue={prod.title}
                            onBlur={(e) => handleProductChange(sIdx, pIdx, "title", e.target.value)}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Brand"
                            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                            defaultValue={prod.brand}
                            onBlur={(e) => handleProductChange(sIdx, pIdx, "brand", e.target.value)}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Image URL"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                            defaultValue={prod.image_url}
                            onBlur={(e) => handleProductChange(sIdx, pIdx, "image_url", e.target.value)}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Divider sx={{ mb: 6, borderColor: "#eaeaea" }} />

        {/* SECTION 3: COLLECTION SECTIONS */}
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#222" }}>Collection Sections</Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              These are the 3 product collection rows (e.g. "Styles for any Event"). Changing the query will fetch new products.
            </Typography>
          </Box>

          {formData.collectionsQueries?.map((col, cIdx) => (
            <Card
              key={cIdx}
              elevation={0}
              sx={{
                mb: 4,
                border: "1px solid #eaeaea",
                borderRadius: "16px",
                overflow: "visible",
                position: "relative"
              }}
            >
              {/* Section Badge */}
              <Box sx={{ position: "absolute", top: -12, left: { xs: 16, sm: 24 } }}>
                <Chip
                  label={`Section ${cIdx + 1}`}
                  size="small"
                  sx={{ backgroundColor: "#5abccb", color: "#fff", fontWeight: 600, fontSize: "12px", height: "24px" }}
                />
              </Box>

              <CardContent sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 5 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Section Heading"
                      defaultValue={col.heading}
                      onBlur={(e) => handleCollectionChange(cIdx, "heading", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      helperText='Displayed as the section title'
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="AI Search Query"
                      defaultValue={col.query}
                      onBlur={(e) => handleCollectionChange(cIdx, "query", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      helperText="Products will be fetched using this query"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      fullWidth
                      label="Gender"
                      value={col.gender}
                      onChange={(e) => handleCollectionChange(cIdx, "gender", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    >
                      <MenuItem value="Women">Women</MenuItem>
                      <MenuItem value="Men">Men</MenuItem>
                      <MenuItem value="Kids">Kids</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomepageContentEditor;
